import React, { useEffect, useState, useRef } from "react";
import { AxiosError } from "axios";
import { Datum, activityNotCovered } from "../../interface/ActivityNotCovered";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import { ActivityNotCoveredAxiosInstance } from "../../Network/ActivityNotCoveredAxiosInstance";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Option } from "../../interface/Option";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import ListOfDoctorName from "../Shared/Doctor";
import FileUpload from "Pages/uploadFile/uploadFileActivityNotCovered/uploadFileActivityNotCovered";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── preserveScroll helper ────────────────────────────────────────────────────
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// ─── EntityCard ───────────────────────────────────────────────────────────────
const colors: Record<string, { border: string; bg: string; text: string; badgeBg: string; checkBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200",    checkBg: "bg-blue-50/30 border-blue-100" },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200",    checkBg: "bg-cyan-50/30 border-cyan-100" },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200",  checkBg: "bg-purple-50/30 border-purple-100" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200",   checkBg: "bg-amber-50/30 border-amber-100" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200", checkBg: "bg-emerald-50/30 border-emerald-100" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200",    checkBg: "bg-pink-50/30 border-pink-100" },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200",  checkBg: "bg-yellow-50/30 border-yellow-100" },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200",  checkBg: "bg-indigo-50/30 border-indigo-100" },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200",   checkBg: "bg-slate-50/30 border-slate-100" },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200",    checkBg: "bg-teal-50/30 border-teal-100" },
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children,
}) => {
  const c = colors[color] ?? colors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclusion
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Inclusion
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── mode conversion helpers ──────────────────────────────────────────────────
const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);
const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ActivityNotCovered: React.FC = () => {
  useQaAutomationIds("activity-not-covered");
  const [items, setItems] = useState<activityNotCovered>({ data: [] });
  const [relationName, setRelationName] = useState<string>("");
  const [customActivityGroups, setCustomActivityGroups] = useState<Option[]>([]);
  const [medicalActivityGroups, setMedicalActivityGroups] = useState<Option[]>([]);
  const [ActivityType, setActivityType] = useState<string>("");
  const [activitiesByType, setActivitiesByType] = useState<{
    CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[];
  }>({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });

  // ── Provider ──
  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  // ── Networks ──
  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);

  // ── Insurance Plan ──
  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  // ── Payer ──
  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  // ── Policy ──
  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);

  // ── Patients ──
  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);

  // ── VIP ──
  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);

  // ── Doctor ──
  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);

  // ── User Level ──
  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  // ── Specialty ──
  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);

  // ── Entities toggle ──
  const [showEntities, setShowEntities] = useState(false);

  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // clear flags
  const [clearNetworks, setClearNetworks] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearActivityCode, setClearActivityCode] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);
  const [clearCustomActivityGroups, setClearCustomActivityGroups] = useState(false);
  const [clearMedicalActivityGroups, setClearMedicalActivityGroups] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);
  const [clearVip, setClearVip] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [selectedActivitiesList, setSelectedActivitiesList] = useState<string[]>([]);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ── Role & Admin ──
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog ──
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);

  const getToken = () => localStorage.getItem("token") || "";
  const { toast } = useToast();

  const getActivityTypeColor = (type: string) => {
    const cols: Record<string, { bg: string; border: string; text: string }> = {
      CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900" },
      HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900" },
      Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
      Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
    };
    return cols[type] || { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-900" };
  };


  // ── Reset all entity states ───────────────────────────────────────────────
  const resetAllEntityStates = () => {
    setProvider([]);      setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setNetworks([]);      setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);         setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);        setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);      setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);           setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setDoctor([]);        setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setUserLevel([]);     setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setSpecialty([]);     setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true);
    setClearNetworks(true);
    setClearInsuranceplan(true);
    setClearPatient(true);
    setClearPolicy(true);
    setClearDoctor(true);
    setClearVip(true);
    setClearSpecialty(true);
  };

  const clear = () => {
    setRelationName("");
    setActivityType("");
    setActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    setCustomActivityGroups([]);
    setMedicalActivityGroups([]);
    setSelectedMainOption(null);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders || provider.length > 0,     label: "Providers" },
      { isActive: isDoctorActive,     hasData: selectAllDoctor    || doctor.length > 0,        label: "Doctor" },
      { isActive: isNetworkActive,    hasData: selectAllNetworks  || networks.length > 0,      label: "Networks" },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy    || policy.length > 0,        label: "Policy" },
      { isActive: isPatientActive,    hasData: selectAllPatients  || patients.length > 0,      label: "Patients" },
      { isActive: isVipActive,        hasData: selectAllVip       || vip.length > 0,           label: "VIP" },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty || specialty.length > 0,     label: "Specialty" },
      { isActive: isPayerActive,      hasData: selectAllPayer     || Payer.length > 0,         label: "Payer" },
      { isActive: isUserActive,       hasData: selectAllUser      || userLevel.length > 0,     label: "User Level" },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  const handleOpenDialog  = (item: Datum) => { setSelectedItem(item); setTimeout(() => { dialogRef.current?.showModal(); }, 0); };
  const handleCloseDialog = () => { dialogRef.current?.close(); setSelectedItem(null); };
  const handleEditClick   = (item: Datum) => { handleEdit(item); setIsDialogOpen(true); };
  const handleAddClick    = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  useEffect(() => { fetchUsers(); }, []);

  const handleInsuranceplanChange         = (s: Option[]) => setInsuranceplan(s);
  const handleNetworkChange               = (s: Option[]) => setNetworks(s);
  const handleCustomActivityGroupsChange  = (s: Option[]) => setCustomActivityGroups(s);
  const handleMedicalActivityGroupsChange = (s: Option[]) => setMedicalActivityGroups(s);
  const handlePoliciesChange              = (s: PolicyItem[]) => setPolicy(s);
  const handlePatientChange               = (s: Option[]) => setPatients(s);
  const handleProviderChange              = (s: Option[]) => setProvider(s);
  const handleDoctorChange                = (s: Option[]) => setDoctor(s);
  const handleVipChange                   = (s: Option[]) => setVip(s);
  const handleSpecialtyChange             = (s: Option[]) => setSpecialty(s);

  // ── fetchItems — sorted, deleted last ────────────────────────────────────

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityNotCoveredAxiosInstance.get(
        "getActivityNotCovered",
        {
          params: {
            page,
            limit,
            name: filters.search.trim() || undefined,
            activity: filters.search.trim() || undefined,
            disease: filters.search.trim() || undefined,
            activity_code: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ data: rows }),
    normalizeRow: (item: any) => {
      const typeMap: Record<string, string> = { cpt: "CPT", hcpcs: "HCPCS", dental: "Dental", drugs: "Drugs" };
      if (Array.isArray(item.activity_type) && Array.isArray(item.activity_code) && item.activity_code.length > 0 && item.activity_type.length > 0) {
        const activity_types = item.activity_code.map((code: string, idx: number) => {
          const rawType = (item.activity_type[idx] || "cpt").toLowerCase();
          return { code, type: typeMap[rawType] || rawType };
        });
        return { ...item, activity_types };
      }
      return { ...item, activity_types: [] };
    },
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Error fetching Activity Not Covered.", error);
      toast({
        title: "Error",
        description: "Error fetching Activity Not Covered.",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const toggleActiveStatus = async (item: Datum) => {
    try {
      const ValuesProvider = (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" }));
      const policyData = (item.policy || []).map((p: any) => ({ id: p.id || 0, Policy_id: p.Policy_id || "", Policy_name: p.Policy_name || "", plans: p.plans || [] }));
      const networksSource = item.networks ?? (item as any).network ?? [];
      const patientsSource = item.patients ?? (item as any).patient ?? [];
      const updatedItem = {
        data: {
          relation_name: item.relation_name,
          activity_type: item.activity_type || "",
          activity_types: item.activity_types || [],
          activity_code: item.activity_code || [],
          custom_groups: (item.custom_groups || []).map((g: any) => g.id ?? g),
          medical_groups: (item.medical_groups || []).map((g: any) => g.id ?? g),
          provider: ValuesProvider,
          provider_mode: item.provider_mode ?? "DISABLED", provider_all: item.provider_all ?? false,
          networks: (Array.isArray(networksSource) ? networksSource : []).map((n: any) => n.id ?? n),
          network_mode: item.network_mode ?? "DISABLED", network_all: item.network_all ?? false,
          insuranceplan: (item.insuranceplan || []).map((i: any) => i.id ?? i),
          insuranceplan_mode: item.insuranceplan_mode ?? "DISABLED", insuranceplan_all: item.insuranceplan_all ?? false,
          payer: item.payer || [],
          payer_mode: item.payer_mode ?? "DISABLED", payer_all: item.payer_all ?? false,
          patients: (Array.isArray(patientsSource) ? patientsSource : []).map((p: any) => p.name ?? p),
          patient_mode: item.patient_mode ?? "DISABLED", patient_all: item.patient_all ?? false,
          policy: policyData,
          policy_mode: item.policy_mode ?? "DISABLED", policy_all: item.policy_all ?? false,
          user_level: item.user_level || [],
          user_mode: item.user_mode ?? "DISABLED", user_all: item.user_all ?? false,
          vip: (item.vip || []).map((v: any) => v.id ?? v),
          vip_mode: item.vip_mode ?? "DISABLED", vip_all: item.vip_all ?? false,
          doctor: (item.doctor || []).map((d: any) => ({ Doctor_id: d.Doctor_id ?? "", Doctor_name: d.Doctor_name ?? d })),
          doctor_mode: item.doctor_mode ?? "DISABLED", doctor_all: item.doctor_all ?? false,
          specialty: (item.specialty || []).map((s: any) => s.id ?? s),
          specialty_mode: item.specialty_mode ?? "DISABLED", specialty_all: item.specialty_all ?? false,
          is_active: !item.is_active, id: item.id,
        },
        token: getToken(),
      };
      const response = await ActivityNotCoveredAxiosInstance.put(`updateActivityNotCovered`, updatedItem);
      if (response.data.success) {
        setItems((prev) => ({ data: prev.data.map((i) => i.id === item.id ? { ...i, is_active: !item.is_active } : i) }));
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ── Soft Delete ───────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotCoveredAxiosInstance.delete(`softDeleteActivityNotCovered/${id}`, { data: { token: getToken() } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Not Covered Deleted Successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Activity Not Covered";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ── Restore ───────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotCoveredAxiosInstance.patch(`restoreActivityNotCovered/${id}`, { token: getToken() });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Not Covered restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore.", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore.", variant: "destructive" });
    }
  };

  // ── Hard Delete (permanent) ───────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteActivityNotCovered/${id}?force=true` : `deleteActivityNotCovered/${id}`;
      const response = await ActivityNotCoveredAxiosInstance.delete(url, { data: { token: getToken() } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Not Covered Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to permanently delete.");
    }
  };

  // ── Force Soft Delete ─────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotCoveredAxiosInstance.delete(`softDeleteActivityNotCovered/${id}?force=true`, { data: { token: getToken() } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Not Covered force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete.");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete.");
    }
  };

  const buildProviderData = (o: any) => {
    if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
    const match = o.label?.match(/^\((.+)\) (.+)$/);
    if (match) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
    return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
  };

  const buildAllActivities = () => {
    const all: any[] = [];
    Object.entries(activitiesByType).forEach(([type, activities]) => {
      if (activities && activities.length > 0)
        activities.forEach((a) => all.push({ code: a.value, type }));
    });
    return all;
  };

  const buildPolicyData = (p: any) => {
    if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
    if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
    if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
    return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
  };

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    const allActivities = buildAllActivities();
    try {
      const newGroupArray = [{
        relation_name: relationName,
        activity_type: allActivities.map((a) => a.type.toLowerCase()),
        activity_code: allActivities.map((a) => a.code),
        activity_types: allActivities.map((a) => ({ code: a.code, type: a.type })),
        custom_groups: customActivityGroups.map((o) => o.value),
        medical_groups: medicalActivityGroups.map((o) => o.value),
        provider: provider.map(buildProviderData),
        provider_mode: cardToMode(providerMode), provider_all: selectAllProviders,
        networks: networks.map((o) => o.value),
        network_mode: cardToMode(networkMode), network_all: selectAllNetworks,
        insuranceplan: insuranceplan.map((o) => Number(o.value)),
        insuranceplan_mode: cardToMode(insuranceMode), insuranceplan_all: selectAllInsurance,
        payer: Payer, payer_mode: cardToMode(payerMode), payer_all: selectAllPayer,
        patients: patients.map((o) => o.label),
        patient_mode: cardToMode(patientMode), patient_all: selectAllPatients,
        policy: policy.map(buildPolicyData),
        policy_mode: cardToMode(policyMode), policy_all: selectAllPolicy,
        user_level: userLevel, user_mode: cardToMode(userMode), user_all: selectAllUser,
        vip: vip.map((o) => o.value), vip_mode: cardToMode(vipMode), vip_all: selectAllVip,
doctor: doctor.map((o) => {
      const name = (o.label || "").replace(/^\([^)]*\)\s*/, "").trim();
      return { Doctor_id: o.value?.toString() || "", Doctor_name: name || (o.label?.toString() || "") };
    }),
        doctor_mode: cardToMode(doctorMode), doctor_all: selectAllDoctor,
        specialty: specialty.map((o) => o.value),
        specialty_mode: cardToMode(specialtyMode), specialty_all: selectAllSpecialty,
        is_active: isActive,
      }];
      const response = await ActivityNotCoveredAxiosInstance.post("addNewActivityNotCovered", { data: newGroupArray, token: getToken() });
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activity Not Covered Group Added Successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    const allActivities = buildAllActivities();
    const updatedItem = {
      data: {
        relation_name: relationName,
        activity_type: allActivities.map((a) => a.type.toLowerCase()),
        activity_code: allActivities.map((a) => a.code),
        activity_types: allActivities.map((a) => ({ code: a.code, type: a.type })),
        custom_groups: customActivityGroups.map((o) => o.value),
        medical_groups: medicalActivityGroups.map((o) => o.value),
        provider: provider.map(buildProviderData),
        provider_mode: cardToMode(providerMode), provider_all: selectAllProviders,
        networks: networks.map((o) => o.value),
        network_mode: cardToMode(networkMode), network_all: selectAllNetworks,
        insuranceplan: insuranceplan.map((o) => Number(o.value)),
        insuranceplan_mode: cardToMode(insuranceMode), insuranceplan_all: selectAllInsurance,
        payer: Payer, payer_mode: cardToMode(payerMode), payer_all: selectAllPayer,
        patients: patients.map((o) => o.label),
        patient_mode: cardToMode(patientMode), patient_all: selectAllPatients,
        policy: policy.map(buildPolicyData),
        policy_mode: cardToMode(policyMode), policy_all: selectAllPolicy,
        user_level: userLevel, user_mode: cardToMode(userMode), user_all: selectAllUser,
        vip: vip.map((o) => o.value), vip_mode: cardToMode(vipMode), vip_all: selectAllVip,
doctor: doctor.map((o) => {
      const name = (o.label || "").replace(/^\([^)]*\)\s*/, "").trim();
      return { Doctor_id: o.value?.toString() || "", Doctor_name: name || (o.label?.toString() || "") };
    }),
        doctor_mode: cardToMode(doctorMode), doctor_all: selectAllDoctor,
        specialty: specialty.map((o) => o.value),
        specialty_mode: cardToMode(specialtyMode), specialty_all: selectAllSpecialty,
        is_active: isActive, id,
      },
      token: getToken(),
    };
    try {
      const response = await ActivityNotCoveredAxiosInstance.put(`updateActivityNotCovered`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear();
        setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity Not Covered Group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    setRelationName(item.relation_name);
    const validActivityTypes: Record<string, string> = { cpt: "CPT", dental: "Dental", hcpcs: "HCPCS", drugs: "Drugs" };

    if (item.activity_type) {
      const typeVal = Array.isArray(item.activity_type) ? (item.activity_type[0] || "") : item.activity_type;
      setActivityType(validActivityTypes[typeVal?.toLowerCase()] || typeVal);
    } else { setActivityType(""); }

    if (item.activity_types && Array.isArray(item.activity_types) && item.activity_types.length > 0) {
      const grouped = { CPT: [] as Option[], HCPCS: [] as Option[], Dental: [] as Option[], Drugs: [] as Option[] };
      item.activity_types.forEach((at: any) => {
        const typeKey = at.type as keyof typeof grouped;
        if (at.type && at.code && grouped[typeKey] !== undefined)
          grouped[typeKey].push({ value: at.code, label: at.code.toString() });
      });
      setActivitiesByType(grouped);
    } else if (item.activity_code && Array.isArray(item.activity_code) && item.activity_code.length > 0) {
      const grouped = { CPT: [] as Option[], HCPCS: [] as Option[], Dental: [] as Option[], Drugs: [] as Option[] };
      const rawType = Array.isArray(item.activity_type) ? (item.activity_type[0] || "") : (item.activity_type || "");
      const typeKey = validActivityTypes[rawType?.toLowerCase()] || "CPT";
      item.activity_code.forEach((code: string) => {
        if (grouped[typeKey as keyof typeof grouped]) grouped[typeKey as keyof typeof grouped].push({ value: code, label: code });
      });
      setActivitiesByType(grouped);
    } else { setActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] }); }

    const CustomActivityGroupsData = Array.isArray(item.custom_groups) ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setCustomActivityGroups(CustomActivityGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const MedicalActivityGroupsData = Array.isArray(item.medical_groups) ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMedicalActivityGroups(MedicalActivityGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));

    const networksSource = item.networks ?? (item as any).network ?? [];
    const networksData = Array.isArray(networksSource) ? networksSource.map((obj: any) => typeof obj === "string" ? { id: 0, network_name: obj } : { id: obj.id || 0, network_name: obj.network_name || "" }) : [];
    const mappedNetworks = networksData.map((obj: any) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.network_name}` : obj.network_name }));
    setNetworks(mappedNetworks);
    const nCard = modeToCard(item.network_mode);
    setNetworkMode(nCard.cardMode); setIsNetworkActive(nCard.isActive || mappedNetworks.length > 0);
    setSelectAllNetworks(item.network_all ?? false);

    const insuranceplanData = Array.isArray(item.insuranceplan) ? item.insuranceplan.map((obj) => typeof obj === "string" ? { id: 0, Plan_name: obj } : { id: (obj as any).id || 0, Plan_name: (obj as any).Plan_name || "" }) : [];
    const mappedInsurance = insuranceplanData.map((obj) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.Plan_name}` : obj.Plan_name }));
    setInsuranceplan(mappedInsurance);
    const iCard = modeToCard(item.insuranceplan_mode);
    setInsuranceMode(iCard.cardMode); setIsInsuranceActive(iCard.isActive || mappedInsurance.length > 0);
    setSelectAllInsurance(item.insuranceplan_all ?? false);

    const ProvidersData = Array.isArray(item.provider) ? item.provider.map((obj) => typeof obj === "string" ? { Provider_id: 0, Provider_name: obj } : { Provider_id: (obj as any).Provider_id || 0, Provider_name: (obj as any).Provider_name || "" }) : [];
    const mappedProviders = ProvidersData.map((obj) => ({ value: obj.Provider_id, label: obj.Provider_id ? `(${obj.Provider_id}) ${obj.Provider_name}` : obj.Provider_name }));
    setProvider(mappedProviders);
    const pCard = modeToCard(item.provider_mode);
    setProviderMode(pCard.cardMode); setIsProviderActive(pCard.isActive || mappedProviders.length > 0);
    setSelectAllProviders(item.provider_all ?? false);

    const patientsSource = item.patients ?? (item as any).patient ?? [];
    const PatientsData = Array.isArray(patientsSource) ? patientsSource.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedPatients = PatientsData.map((obj: any) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name }));
    setPatients(mappedPatients);
    const patCard = modeToCard(item.patient_mode);
    setPatientMode(patCard.cardMode); setIsPatientActive(patCard.isActive || mappedPatients.length > 0);
    setSelectAllPatients(item.patient_all ?? false);

    const mappedPolicy = Array.isArray(item.policy) ? item.policy.map((obj) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] })) : [];
    setPolicy(mappedPolicy);
    const polCard = modeToCard(item.policy_mode);
    setPolicyMode(polCard.cardMode); setIsPolicyActive(polCard.isActive || mappedPolicy.length > 0);
    setSelectAllPolicy(item.policy_all ?? false);

    const vipData = Array.isArray(item.vip) ? item.vip.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedVip = vipData.map((obj) => ({ value: obj.id, label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name }));
    setVip(mappedVip);
    const vCard = modeToCard(item.vip_mode);
    setVipMode(vCard.cardMode); setIsVipActive(vCard.isActive || mappedVip.length > 0);
    setSelectAllVip(item.vip_all ?? false);

const DoctorsData = Array.isArray(item.doctor) ? item.doctor.map((obj) => {
  if (typeof obj === "string") {
    const parts = (obj as string).split(",");
    if (parts.length === 2) {
      return { Doctor_id: parts[0].trim(), Doctor_name: parts[1].trim() };
    }
    return { Doctor_id: 0, Doctor_name: obj };
  }
  return { Doctor_id: (obj as any).Doctor_id || 0, Doctor_name: (obj as any).Doctor_name || "" };
}) : [];
    const mappedDoctors = DoctorsData.map((obj) => ({ value: obj.Doctor_id, label: obj.Doctor_id ? `(${obj.Doctor_id}) ${obj.Doctor_name}` : obj.Doctor_name }));
    setDoctor(mappedDoctors);
    const dCard = modeToCard(item.doctor_mode);
    setDoctorMode(dCard.cardMode); setIsDoctorActive(dCard.isActive || mappedDoctors.length > 0);
    setSelectAllDoctor(item.doctor_all ?? false);

    setPayer(item.payer || []);
    const payCard = modeToCard(item.payer_mode);
    setPayerMode(payCard.cardMode); setIsPayerActive(payCard.isActive || (item.payer?.length > 0));
    setSelectAllPayer(item.payer_all ?? false);

    setUserLevel(item.user_level || []);
    const uCard = modeToCard(item.user_mode);
    setUserMode(uCard.cardMode); setIsUserActive(uCard.isActive || (item.user_level?.length > 0));
    setSelectAllUser(item.user_all ?? false);

    const specialtyData = Array.isArray(item.specialty) ? item.specialty.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    const mappedSpecialty = specialtyData.map((obj) => ({ value: (obj as any).id, label: (obj as any).id ? `(${(obj as any).id}) ${(obj as any).name}` : (obj as any).name }));
    setSpecialty(mappedSpecialty);
    const spCard = modeToCard(item.specialty_mode);
    setSpecialtyMode(spCard.cardMode); setIsSpecialtyActive(spCard.isActive || mappedSpecialty.length > 0);
    setSelectAllSpecialty(item.specialty_all ?? false);

    setIsActive(item.is_active);
    setEditIndex(item.id);

    const networksSourceCheck = item.networks ?? (item as any).network ?? [];
    const patientsSourceCheck = item.patients ?? (item as any).patient ?? [];
    const hasEntities = !!(
      item.provider?.length > 0 || item.doctor?.length > 0 ||
      (networksSourceCheck as any[])?.length > 0 || item.insuranceplan?.length > 0 ||
      item.policy?.length > 0 || (patientsSourceCheck as any[])?.length > 0 ||
      item.vip?.length > 0 || item.payer?.length > 0 ||
      item.user_level?.length > 0 || item.specialty?.length > 0 ||
      (item.provider_mode      && item.provider_mode      !== "DISABLED") ||
      (item.network_mode       && item.network_mode       !== "DISABLED") ||
      (item.insuranceplan_mode && item.insuranceplan_mode !== "DISABLED") ||
      (item.payer_mode         && item.payer_mode         !== "DISABLED") ||
      (item.policy_mode        && item.policy_mode        !== "DISABLED") ||
      (item.patient_mode       && item.patient_mode       !== "DISABLED") ||
      (item.vip_mode           && item.vip_mode           !== "DISABLED") ||
      (item.doctor_mode        && item.doctor_mode        !== "DISABLED") ||
      (item.user_mode          && item.user_mode          !== "DISABLED") ||
      (item.specialty_mode     && item.specialty_mode     !== "DISABLED")
    );
    setShowEntities(hasEntities);

    if (item.activity_types && item.activity_types.length > 0) setSelectedMainOption("list_of_Activity_code");
    else if (item.activity_code && item.activity_code.length > 0) setSelectedMainOption("list_of_Activity_code");
    else if (item.medical_groups && item.medical_groups.length > 0) setSelectedMainOption("medical_Activity_groups");
    else if (item.custom_groups && item.custom_groups.length > 0) setSelectedMainOption("custom_Activity_groups");
    else setSelectedMainOption(null);
  };

  const handleDownload = (item: Datum) => {
    const rows = ["relation_name;activity_type;activity_code;medical_groups;custom_groups;provider;provider_mode;provider_all;networks;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patients;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;specialty;specialty_mode;specialty_all;is_active"];
    const activityCodes    = item.activity_code || [];
    const medicalGroups    = item.medical_groups || [];
    const customGroups     = item.custom_groups || [];
    const providers        = item.provider || [];
    const networksArr      = item.networks ?? (item as any).network ?? [];
    const patientsArr      = item.patients ?? (item as any).patient ?? [];
    const insuranceplanArr = item.insuranceplan || [];
    const policyArr        = item.policy || [];
    const userLevelArr     = item.user_level || [];
    const doctorArr        = item.doctor || [];
    const payerArr         = item.payer || [];
    const vipArr           = item.vip || [];
    const specialtyArr     = item.specialty || [];
    const maxLen = Math.max(activityCodes.length, medicalGroups.length, customGroups.length, providers.length, networksArr.length, insuranceplanArr.length, patientsArr.length, policyArr.length, userLevelArr.length, doctorArr.length, payerArr.length, vipArr.length, specialtyArr.length);
    for (let index = 0; index < Math.max(1, maxLen); index++) {
      rows.push([
        item.relation_name || "", item.activity_type || "",
        index < activityCodes.length ? activityCodes[index] : "",
        index < medicalGroups.length ? medicalGroups[index].name || "" : "",
        index < customGroups.length ? customGroups[index].name || "" : "",
        index < providers.length ? providers[index].Provider_name || "" : "",
        item.provider_mode ?? "DISABLED", item.provider_all ? "true" : "false",
        index < networksArr.length ? networksArr[index].network_name || "" : "",
        item.network_mode ?? "DISABLED", item.network_all ? "true" : "false",
        index < insuranceplanArr.length ? insuranceplanArr[index].Plan_name || "" : "",
        item.insuranceplan_mode ?? "DISABLED", item.insuranceplan_all ? "true" : "false",
        index < payerArr.length ? payerArr[index] : "",
        item.payer_mode ?? "DISABLED", item.payer_all ? "true" : "false",
        index < patientsArr.length ? patientsArr[index].name || "" : "",
        item.patient_mode ?? "DISABLED", item.patient_all ? "true" : "false",
        index < policyArr.length ? policyArr[index].Policy_name || "" : "",
        item.policy_mode ?? "DISABLED", item.policy_all ? "true" : "false",
        index < userLevelArr.length ? userLevelArr[index] : "",
        item.user_mode ?? "DISABLED", item.user_all ? "true" : "false",
        index < doctorArr.length ? doctorArr[index].Doctor_name || "" : "",
        item.doctor_mode ?? "DISABLED", item.doctor_all ? "true" : "false",
        index < vipArr.length ? vipArr[index].name || "" : "",
        item.vip_mode ?? "DISABLED", item.vip_all ? "true" : "false",
        index < specialtyArr.length ? (specialtyArr[index] as any).name || "" : "",
        item.specialty_mode ?? "DISABLED", item.specialty_all ? "true" : "false",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ActivityNotCovered${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
    cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        const val = row.getValue("relation_name") as string;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {Array.isArray(val) ? val.join(", ") : val}
          </div>
        );
      },
    },
    {
      accessorKey: "activity_code",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        let activityItems: Array<{
          code: string;
          type: string;
          kind: "activity";
        }> = [];

        if (
          item.activity_types &&
          Array.isArray(item.activity_types) &&
          item.activity_types.length > 0
        ) {
          activityItems = item.activity_types
            .map((activity: any) => ({
              code: String(activity.code ?? ""),
              type: String(activity.type ?? "Unknown"),
              kind: "activity" as const,
            }))
            .filter((activity) => activity.code);
        } else if (
          item.activity_code &&
          Array.isArray(item.activity_code) &&
          item.activity_code.length > 0
        ) {
          const validTypes: Record<string, string> = {
            cpt: "CPT",
            dental: "Dental",
            hcpcs: "HCPCS",
            drugs: "Drugs",
          };

          const activityTypes = Array.isArray(item.activity_type)
            ? item.activity_type
            : item.activity_code.map(() => item.activity_type);

          activityItems = item.activity_code.map((code: any, index: number) => {
            const rawType = String(activityTypes[index] ?? "Unknown");
            const displayType =
              validTypes[rawType.toLowerCase()] || rawType || "Unknown";

            return {
              code: String(code),
              type: displayType,
              kind: "activity" as const,
            };
          });
        }

        const medicalItems = Array.isArray(item.medical_groups)
          ? item.medical_groups.map((group: any) => ({
              code: String(group?.name ?? group),
              type: "Medical Group",
              kind: "medical" as const,
            }))
          : [];

        const customItems = Array.isArray(item.custom_groups)
          ? item.custom_groups.map((group: any) => ({
              code: String(group?.name ?? group),
              type: "Custom Group",
              kind: "custom" as const,
            }))
          : [];

        const allItems = [
          ...activityItems,
          ...medicalItems,
          ...customItems,
        ];

        if (allItems.length === 0) {
          return (
            <div className="w-full text-center text-gray-400">
              -
            </div>
          );
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingItems = hasSearch
          ? allItems.filter((entry) =>
              entry.code.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatches = hasSearch && matchingItems.length > 0;
        const displaySource = hasMatches ? matchingItems : allItems;
        const displayedItems = displaySource.slice(0, 3);

        const remainingCount = Math.max(
          0,
          displaySource.length - displayedItems.length,
        );

        const getItemColor = (
          entry: (typeof allItems)[number],
          isMatching: boolean,
        ) => {
          if (isMatching) {
            return {
              bg: "bg-yellow-200",
              border: "border-yellow-400",
              text: "text-yellow-900",
            };
          }

          if (entry.kind === "medical") {
            return {
              bg: "bg-green-100",
              border: "border-green-400",
              text: "text-green-900",
            };
          }

          if (entry.kind === "custom") {
            return {
              bg: "bg-pink-100",
              border: "border-pink-400",
              text: "text-pink-900",
            };
          }

          return getActivityTypeColor(entry.type);
        };

        const dialogItems = (hasMatches ? matchingItems : allItems).map(
          (entry) => `${entry.code}|||${entry.type}`,
        );

        return (
          <div className="w-full px-3 py-2">
            <div className="flex flex-wrap justify-center gap-2 mb-2">
              {displayedItems.map((entry, index) => {
                const isMatching =
                  hasSearch &&
                  entry.code.toLowerCase().includes(searchTerm);

                const itemColor = getItemColor(entry, isMatching);

                return (
                  <div
                    key={`${entry.kind}-${entry.code}-${index}`}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${itemColor.bg} ${itemColor.border}`}
                  >
                    <span
                      className={`font-semibold text-sm ${itemColor.text}`}
                    >
                      {entry.code}
                    </span>

                    <span
                      className={`text-[10px] font-bold capitalize ${itemColor.text}`}
                    >
                      {entry.type}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-xs font-bold text-gray-600">
                {hasMatches
                  ? `${matchingItems.length}/${allItems.length}`
                  : allItems.length}
              </span>

              {remainingCount > 0 && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedActivitiesList(dialogItems);
                    setActivityDialogOpen(true);
                  }}
                  className={`text-xs font-semibold flex items-center gap-1 ${
                    hasMatches
                      ? "text-yellow-600 hover:text-yellow-800"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  <span>+{remainingCount} more</span>
                  <i className="fa-solid fa-plus text-[10px]"></i>
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center font-normal">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  const modeBadge = (mode: string | undefined) => {
    if (!mode || mode === "DISABLED") return null;
    const isExclude = mode === "EXCLUDED";
    return (
      <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fee2e2" : "#dcfce7", color: isExclude ? "#991b1b" : "#166534", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
        {isExclude ? "Exclusion" : "Inclusion"}
      </span>
    );
  };

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="activity-not-covered">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900 whitespace-nowrap">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white shrink-0">
            <i className="fa-solid fa-times-circle"></i>
          </span>
          Activity Not Covered
        </h5>
      </div>

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[860px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              {/* ── Header ── */}
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-times-circle" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0 }}>{selectedItem.relation_name}</h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {/* ── Deleted Badge ── */}
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.2)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0 }}>Activity Not Covered — <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
                {(() => {
                  const groupedByType: Record<string, Array<string | number>> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                  if (selectedItem.activity_types && Array.isArray(selectedItem.activity_types) && selectedItem.activity_types.length > 0) {
                    selectedItem.activity_types.forEach((at: any) => { if (at.type && at.code && groupedByType[at.type] !== undefined) groupedByType[at.type].push(at.code); });
                  } else if (selectedItem.activity_code && Array.isArray(selectedItem.activity_code)) {
                    const validTypes: Record<string, string> = { cpt: "CPT", dental: "Dental", hcpcs: "HCPCS", drugs: "Drugs" };
                    const rawType = Array.isArray(selectedItem.activity_type) ? (selectedItem.activity_type[0] || "") : (selectedItem.activity_type || "");
                    const typeKey = validTypes[rawType?.toLowerCase()] || "CPT";
                    selectedItem.activity_code.forEach((code: any) => { if (groupedByType[typeKey]) groupedByType[typeKey].push(code); });
                  }
                  const totalCodes = Object.values(groupedByType).flat().length;
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                      {[
                        { icon: "fa-code", label: "Activity Codes", value: `${totalCodes}`, accent: "#f87171" },
                        { icon: "fa-hospital", label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                        { icon: "fa-puzzle-piece", label: "Custom Groups", value: `${(selectedItem.custom_groups || []).length}`, accent: "#34d399" },
                      ].map((card, i) => (
                        <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                            <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                            <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500 }}>{card.label}</span>
                          </div>
                          <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* ── Scrollable body ── */}
              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "20px 28px" }}>

                {/* ── 1. Activity Configuration ── */}
                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Configuration</span>
                  {(() => {
                    const KNOWN = ["CPT", "HCPCS", "Dental", "Drugs"];
                    const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [], ICD: [] };
                    if (selectedItem.activity_types && Array.isArray(selectedItem.activity_types) && selectedItem.activity_types.length > 0) {
                      selectedItem.activity_types.forEach((at: any) => {
                        const t = at.type || "";
                        const key = KNOWN.includes(t) ? t : "ICD";
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(at.code);
                      });
                    } else if (Array.isArray(selectedItem.activity_code) && selectedItem.activity_code.length > 0) {
                      const validTypes: Record<string, string> = { cpt: "CPT", dental: "Dental", hcpcs: "HCPCS", drugs: "Drugs" };
                      const rawType = Array.isArray(selectedItem.activity_type) ? (selectedItem.activity_type[0] || "") : (selectedItem.activity_type || "");
                      const typeKey = validTypes[rawType?.toLowerCase()] || "ICD";
                      if (!grouped[typeKey]) grouped[typeKey] = [];
                      selectedItem.activity_code.forEach((code: any) => grouped[typeKey].push(code));
                    }
                    const medicalGroups = Array.isArray(selectedItem.medical_groups) ? selectedItem.medical_groups.map((g: any) => g.name || String(g)) : [];
                    const customGroups  = Array.isArray(selectedItem.custom_groups)  ? selectedItem.custom_groups.map((g: any)  => g.name || String(g)) : [];
                    const hasAnything = Object.values(grouped).some(a => a.length > 0) || medicalGroups.length > 0 || customGroups.length > 0;
                    if (!hasAnything) return <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, fontStyle: "italic" }}>No activity codes configured.</p>;

                    const typeStyle: Record<string, { bg: string; color: string; border: string }> = {
                      CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
                      HCPCS:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
                      Dental: { bg: "#faf5ff", color: "#7c3aed", border: "#e9d5ff" },
                      Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
                      ICD:    { bg: "#fefce8", color: "#92400e", border: "#fde68a" },
                    };
                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        {Object.entries(grouped).map(([type, codes]) => codes.length === 0 ? null : (
                          <div key={type} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>{type} Codes ({codes.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                              {codes.map((code, i) => {
                                const s = typeStyle[type] || typeStyle.ICD;
                                return <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: s.bg, color: s.color, border: `0.5px solid ${s.border}` }}>{code}</span>;
                              })}
                            </div>
                          </div>
                        ))}
                        {medicalGroups.length > 0 && (
                          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Medical Groups ({medicalGroups.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                              {medicalGroups.map((g: string, i: number) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#faf5ff", color: "#7c3aed", border: "0.5px solid #e9d5ff" }}>{g}</span>)}
                            </div>
                          </div>
                        )}
                        {customGroups.length > 0 && (
                          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Custom Groups ({customGroups.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                              {customGroups.map((g: string, i: number) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" }}>{g}</span>)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── 2. Target Scope ── */}
                <div style={{ height: 1, background: "#e2e8f0", marginBottom: 20 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12, marginBottom: 20 }}>
                  {[
                    { label: "Providers",       icon: "fa-hospital",          color: "#3b82f6", items: (selectedItem.provider     || []).map((p: any) => p.Provider_id ? `(${p.Provider_id}) ${p.Provider_name}` : p.Provider_name || String(p)),                    mode: selectedItem.provider_mode,      all: (selectedItem as any).provider_all },
                    { label: "Doctors",         icon: "fa-stethoscope",       color: "#06b6d4", items: (selectedItem.doctor       || []).map((d: any) => d.Doctor_id ? `(${d.Doctor_id}) ${d.Doctor_name}` : d.Doctor_name || String(d)),                    mode: selectedItem.doctor_mode,        all: (selectedItem as any).doctor_all },
                    { label: "Networks",        icon: "fa-network-wired",     color: "#8b5cf6", items: (selectedItem.networks ?? (selectedItem as any).network ?? []).map((n: any) => n.id ? `(${n.id}) ${n.network_name}` : n.network_name || String(n)), mode: selectedItem.network_mode, all: (selectedItem as any).network_all },
                    { label: "Insurance Plans", icon: "fa-shield-halved",     color: "#f59e0b", items: (selectedItem.insuranceplan|| []).map((i: any) => i.id ? `(${i.id}) ${i.Plan_name}` : i.Plan_name || String(i)),                    mode: selectedItem.insuranceplan_mode, all: (selectedItem as any).insuranceplan_all },
                    { label: "Policies",        icon: "fa-file-lines",        color: "#10b981", items: (selectedItem.policy       || []).map((p: any) => p.Policy_id ? `(${p.Policy_id}) ${p.Policy_name}` : p.Policy_name || ""), mode: selectedItem.policy_mode,        all: (selectedItem as any).policy_all },
                    { label: "Patients",        icon: "fa-users",             color: "#ec4899", items: (selectedItem.patients ?? (selectedItem as any).patient ?? []).map((p: any) => p.id ? `(${p.id}) ${p.name}` : p.name || String(p)), mode: selectedItem.patient_mode,       all: (selectedItem as any).patient_all },
                    { label: "VIP",             icon: "fa-star",              color: "#eab308", items: (selectedItem.vip          || []).map((v: any) => v.id ? `(${v.id}) ${v.name}` : v.name || String(v)),                    mode: selectedItem.vip_mode,           all: (selectedItem as any).vip_all },
                    { label: "Specialty",       icon: "fa-briefcase-medical", color: "#0d9488", items: (selectedItem.specialty    || []).map((s: any) => s.id ? `(${s.id}) ${s.name}` : s.name || String(s)),                    mode: selectedItem.specialty_mode,     all: (selectedItem as any).specialty_all },
                    { label: "Payer",           icon: "fa-building-columns",  color: "#6366f1", items: (selectedItem.payer        || []).map((p: any) => String(p)),                                        mode: selectedItem.payer_mode,         all: (selectedItem as any).payer_all },
                  ].map((e) => {
                    const isExclude  = e.mode === "EXCLUDED";
                    const isDisabled = !e.mode || e.mode === "DISABLED";
                    return (
                      <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <i className={`fa-solid ${e.icon}`} style={{ fontSize: 11, color: e.color }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                          </div>
                          {!isDisabled && (
                            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                              {isExclude ? "Exclusion" : "Inclusion"}
                            </span>
                          )}
                        </div>
                        {e.all
                          ? <span style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>All {e.label}</span>
                          : e.items.length > 0
                            ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>{e.items.map((item: string, i: number) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>)}</div>
                            : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                        }
                      </div>
                    );
                  })}
                </div>

                {/* ── 3. Audit Trail ── */}
                <div style={{ height: 1, background: "#e2e8f0", marginBottom: 20 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created By</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff" }}>
                        {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Created On</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8" }}>Last Updated</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                        {(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

       
              {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
       {/* Footer */}
              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10 }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

   {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activity Not Covered" : "Edit Activity Not Covered "}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and status of the group">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity configuration" subtitle="Which activity codes or groups this rule covers">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { key: "list_of_Activity_code", label: "Activity Codes", icon: "fa-code", count: activitiesByType.CPT.length + activitiesByType.HCPCS.length + activitiesByType.Dental.length + activitiesByType.Drugs.length },
                  { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: medicalActivityGroups.length },
                  { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: customActivityGroups.length },
                ].map((opt) => {
                  const isSelected = selectedMainOption === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSelectedMainOption(isSelected ? null : opt.key)}
                      className={`relative flex flex-col items-center gap-2 rounded-xl border-2 px-3 py-4 transition-all ${isSelected ? "border-blue-500 bg-blue-50 shadow-sm" : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"}`}
                    >
                      {opt.count > 0 && (
                        <span className={`absolute top-2 right-2 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                          {opt.count}
                        </span>
                      )}
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSelected ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                        <i className={`fa-solid ${opt.icon} text-sm`}></i>
                      </span>
                      <span className={`text-xs font-semibold text-center ${isSelected ? "text-blue-700" : "text-gray-500"}`}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>

              {selectedMainOption === "list_of_Activity_code" && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity Codes by Type</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ key: "CPT", bg: "blue" }, { key: "HCPCS", bg: "green" }, { key: "Dental", bg: "purple" }, { key: "Drugs", bg: "orange" }].map(({ key, bg }) => (
                      <div key={key} className={`bg-white p-3 rounded-lg border border-${bg}-200`}>
                        <Label className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 bg-${bg}-100 text-${bg}-900 rounded text-xs font-bold border border-${bg}-400`}>{key}</span>
                          <span className="text-xs text-gray-500">({activitiesByType[key as keyof typeof activitiesByType].length} codes)</span>
                        </Label>
                        <ListofActivityCode onChange={(selected: Option[]) => setActivitiesByType((prev) => ({ ...prev, [key]: selected }))} type={key} defaultValue={activitiesByType[key as keyof typeof activitiesByType]} clear={false} isValid={true} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedMainOption === "medical_Activity_groups" && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <ListOfActivityMedical onChange={handleMedicalActivityGroupsChange} defaultValue={medicalActivityGroups} clear={clearMedicalActivityGroups} />
                </div>
              )}
              {selectedMainOption === "custom_Activity_groups" && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <ListOfActivityCustom onChange={handleCustomActivityGroupsChange} defaultValue={customActivityGroups} clear={clearCustomActivityGroups} />
                </div>
              )}
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Inclusion</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclusion</span> = apply to everyone except selected items
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <ListOfProviderName onChange={handleProviderChange} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctorMode(null); setIsDoctorActive(false); } else { setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctorMode(null); setIsDoctorActive(false); } else { setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={handleDoctorChange} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    <EntityCard label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <ListOfSpecialty onChange={handleSpecialtyChange} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={handleNetworkChange} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={handleInsuranceplanChange} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <PolicyNames onChange={handlePoliciesChange} defaultValue={policy} clear={clearPolicy} />}
                    </EntityCard>

                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <VipPatientSelector onChange={handlePatientChange} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <Vip onChange={handleVipChange} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => { if (editIndex === null) { addItem(); } else { updateItem(editIndex); } }} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ══ ALL CODES DIALOG ═════════════════════════════════════════════════ */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list"></i> All Activity Codes
              <span className="ml-auto text-sm font-normal text-gray-600">({selectedActivitiesList?.length || 0} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)] pr-2">
            <div className="grid grid-cols-4 gap-3">
              {selectedActivitiesList && selectedActivitiesList.map((item, index) => {
                const [code, type] = item.split("|||");
                const clrs = getActivityTypeColor(type);
                return (
                  <div key={index} className={`flex items-center justify-between gap-2 p-3 bg-white rounded-lg border hover:shadow-md transition-all ${clrs.border}`}>
                    <span className={`font-bold text-base ${clrs.text}`}>{code}</span>
                    <div className={`w-px h-6 ${clrs.border}`}></div>
                    <span className={`text-xs font-bold capitalize ${clrs.text} opacity-75`}>{type}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button onClick={() => setActivityDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ DATA TABLE ═══════════════════════════════════════════════════════ */}
      <DataTable2
        qaIdPrefix="activity-not-covered-data-table"
        columns={columns}
        data={items.data}
        searchColumns={[]}
        addButtonLabel="Add New Relation"
        validateButtonLabel="Validate"
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        showAddButton={false}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.data.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <div style={{ width: 1, height: 28, background: "#cbd5e1", flexShrink: 0 }} />
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity Code, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1", flexShrink: 0 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.value === "true" && filterStatus === "true" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ══ FORCE / HARD DELETE DIALOG ═══════════════════════════════════════ */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> this record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 6 }}></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete */}
              {forceAction === "softDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Record Is In Use</h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force delete</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && forceSoftDelete(forceId)}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt" style={{ marginRight: 6 }}></i>Force Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityNotCovered;
